import {
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Td,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaEllipsisV, FaFileDownload, FaPlus, FaSignature, FaTimes, FaTrashAlt } from "react-icons/fa";
import { TextDocumentDto, useDeleteTextDocumentMutation } from "../../autogen/bff-api";
import { MainDocumentContext } from "../../pages/contracts/edit/docs/signatures/CreateSignedDocument";
import { displayDateWithTime } from "../formatting/displayDateWithTime";
import { downloadTextDocument } from "../input/TipTap/downloadTextDocument";
import { isTextDocument } from "./utils";

export const TextDocumentRow = ({
  doc,
  setTextDocumentToView,
  canEditName,
  onUpdateDocumentMeta,
  setDocumentToSign,
  canDeleteDocument,
  isTagsColumnVisible,
  isCategoryColumnVisible = false,
  isCreatedByColumnVisible,
  canEditTags,
}: {
  doc: TextDocumentDto;
  setTextDocumentToView: Dispatch<SetStateAction<TextDocumentDto | undefined>>;
  canEditName: boolean;
  onUpdateDocumentMeta: ({ document, name, tags }: { document: TextDocumentDto; name: string; tags: string[] }) => void;
  setDocumentToSign?: Dispatch<SetStateAction<MainDocumentContext | undefined>>;
  canDeleteDocument: boolean;
  isTagsColumnVisible: boolean;
  isCategoryColumnVisible?: boolean;
  isCreatedByColumnVisible: boolean;
  canEditTags: boolean;
}) => {
  const toast = useToast();
  const { t } = useTranslation();

  const [deleteTextDocument, { isLoading: isDeletingDocument }] = useDeleteTextDocumentMutation();

  const [newTag, setNewTag] = useState<string>();
  const [showConfirmDeletion, setShowConfirmDeletion] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);

  return (
    <Tr
      key={doc.id}
      onClick={() => setTextDocumentToView(doc)}
      _hover={{ backgroundColor: "smBackgroundSecondary" }}
      cursor={isTextDocument(doc) ? "pointer" : "auto"}
    >
      <Td px="3">
        <Input
          size="sm"
          rounded="md"
          fontWeight="normal"
          minWidth={{ base: "60", lg: "80" }}
          value={doc.name}
          _focus={{
            borderColor: "default",
            boxShadow: "default",
          }}
          _disabled={{ border: "none" }}
          isDisabled={!canEditName}
          onChange={(e) => onUpdateDocumentMeta({ document: doc, name: e.target.value, tags: doc.tags })}
          onClick={canEditName ? (e) => e.stopPropagation() : undefined}
          cursor={!canEditName ? "pointer" : "auto"}
          pointerEvents={canEditName ? undefined : "none"}
        />
      </Td>
      <Td fontSize="sm">{displayDateWithTime(doc.createdAt)}</Td>
      {isCreatedByColumnVisible && (
        <Td fontSize="sm">{doc.createdBy ? `${doc.createdBy?.firstName} ${doc.createdBy?.lastName}` : ""}</Td>
      )}
      {isTagsColumnVisible && (
        <Td>
          <Flex flexWrap="wrap" columnGap="1" rowGap="1" width="48" maxWidth="48">
            {doc.tags.map((tag, i) => (
              <Tag key={`${tag}-${i}`} size="sm" backgroundColor="smSecondarySoft" onClick={(e) => e.stopPropagation()}>
                <TagLabel>{tag}</TagLabel>
                {canEditTags && (
                  <TagCloseButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateDocumentMeta({ document: doc, name: doc.name, tags: doc.tags.filter((_, j) => i !== j) });
                      setNewTag(undefined);
                    }}
                  />
                )}
              </Tag>
            ))}
            {canEditTags && newTag === undefined && (
              <IconButton
                aria-label="add tag"
                variant="ghost"
                size="xs"
                colorScheme="cyan"
                icon={<Icon as={FaPlus} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setNewTag("");
                }}
              />
            )}
            {newTag !== undefined && (
              <InputGroup display="inline-flex" width="40">
                <Input
                  size="xs"
                  rounded="md"
                  pr="6"
                  value={newTag}
                  autoFocus={true}
                  onChange={(e) => setNewTag(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <InputRightElement width="12" height="6">
                  <IconButton
                    height="4"
                    size="xs"
                    aria-label="save tag"
                    icon={<Icon as={FaTimes} />}
                    rounded="none"
                    variant="ghost"
                    color="smMuted"
                    p="0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewTag(undefined);
                    }}
                  />
                  <IconButton
                    size="xs"
                    aria-label="save tag"
                    icon={<Icon as={FaCheck} />}
                    roundedLeft="none"
                    colorScheme="teal"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateDocumentMeta({ document: doc, name: doc.name, tags: [...(doc.tags ?? []), newTag] });
                      setNewTag(undefined);
                    }}
                  />
                </InputRightElement>
              </InputGroup>
            )}
          </Flex>
        </Td>
      )}
      {isCategoryColumnVisible && <Td />}
      <Td>
        <Menu
          isOpen={isActionMenuOpen}
          onOpen={() => setIsActionMenuOpen(true)}
          onClose={() => {
            setIsActionMenuOpen(false);
            setShowConfirmDeletion(false);
          }}
          closeOnSelect={false}
        >
          <MenuButton
            aria-label="actions"
            as={IconButton}
            size="sm"
            variant="ghost"
            icon={<Icon as={FaEllipsisV} />}
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList>
            <MenuItem
              icon={<Icon as={FaFileDownload} />}
              onClick={async (e) => {
                e.stopPropagation();
                if (!doc.text) {
                  toast({
                    variant: "subtle",
                    status: "warning",
                    title: t("Cannot download empty document"),
                  });
                  return;
                }
                setIsDownloadingDocument(true);
                await downloadTextDocument({ fileName: doc.name, content: doc.text }).catch(() =>
                  toast({ variant: "subtle", status: "error", title: t("Failed to download document") })
                );
                setIsDownloadingDocument(false);
                setIsActionMenuOpen(false);
              }}
            >
              {t("Download")} {isDownloadingDocument && <Spinner size="sm" ml="2" />}
            </MenuItem>
            {setDocumentToSign && (
              <MenuItem
                aria-label="sign document"
                icon={<Icon as={FaSignature} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setDocumentToSign({ textDocument: doc });
                  setIsActionMenuOpen(false);
                }}
              >
                {t("Sign document")}
              </MenuItem>
            )}
            {canDeleteDocument && !showConfirmDeletion && (
              <MenuItem
                icon={<Icon as={FaTrashAlt} color="smDanger" />}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirmDeletion(true);
                }}
              >
                {t("Delete document")}
              </MenuItem>
            )}
            {showConfirmDeletion && (
              <MenuItem
                as={Button}
                size="sm"
                variant="unstyled"
                color="smBackground"
                backgroundColor="smDanger"
                fontWeight="normal"
                icon={<Icon as={FaTrashAlt} color="smBackground" />}
                rightIcon={
                  <Icon
                    as={FaTimes}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowConfirmDeletion(false);
                    }}
                  />
                }
                onClick={async (e) => {
                  e.stopPropagation();
                  await deleteTextDocument(doc);
                  setIsActionMenuOpen(false);
                }}
              >
                {t("Confirm")} {isDeletingDocument && <Spinner size="sm" color="smBackground" ml="2" />}
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </Td>
    </Tr>
  );
};
